import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { getAccountPendingBillsReports } from '../services/api/implementation/impl';
import Loader from '../../shared/components/ui/Loader';
import Modal from '../../shared/components/ui/Modal';
import PaginationComponent from '../../shared/components/PaginationComponent';
import {
  EmptyRow,
  FilterBar,
  Table,
  TableCard,
  TBody,
  Td,
  Th,
  THead,
  Tile,
  TileRow
} from '../../shared/components/ui/primitives';
import { money, shortDate } from './SiteWallet/siteWalletUi';
import { RecordPaymentForm } from './PerformaInvoiceParts';
import {
  BillsFilterFields,
  KIND_TEXT,
  KindPill,
  useBillsFilters,
  useCsvButton
} from './billsReportUi';

const PAGE_SIZE = 25;

const BUCKETS = [
  { value: 'not_due', label: 'Not yet due', tone: 'neutral' },
  { value: '0_30', label: '0–30 days', tone: 'info' },
  { value: '31_60', label: '31–60 days', tone: 'warning' },
  { value: '61_90', label: '61–90 days', tone: 'warning' },
  { value: '90_plus', label: '90+ days', tone: 'danger' }
];
const BUCKET_LABEL = Object.fromEntries(BUCKETS.map((b) => [b.value, b.label]));

const Overdue = ({ days }) => {
  if (days < 0)
    return (
      <span className="text-gray-500">
        In {-days} day{days === -1 ? '' : 's'}
      </span>
    );
  if (days === 0) return <span className="text-gray-700">Due today</span>;
  return (
    <span
      className={days > 60 ? 'font-semibold text-danger-fg' : days > 30 ? 'text-warning-fg' : ''}
    >
      {days} day{days === 1 ? '' : 's'}
    </span>
  );
};

/**
 * Everything still owed — vendor PIs, service provider bills, wallet
 * reimbursements — most overdue first, aged into buckets. A vendor PI can be
 * paid from here.
 */
const PendingBills = () => {
  const userId = Cookies.get('userId');
  const queryClient = useQueryClient();
  const { filters, setFilters, set, typed, setTyped, filtered, clear } = useBillsFilters({
    bucket: ''
  });
  const [page, setPage] = useState(1);
  const [paying, setPaying] = useState(null);
  const [formDirty, setFormDirty] = useState(false);
  useEffect(() => setPage(1), [filters]);

  const pending = useQuery({
    queryKey: ['accountPendingBills', page, filters],
    queryFn: () => getAccountPendingBillsReports({ ...filters, page, limit: PAGE_SIZE }),
    keepPreviousData: true
  });
  const rows = pending.data?.data ?? [];
  const meta = pending.data?.meta ?? {};
  const totals = meta.totals ?? {};
  const byBucket = totals.by_bucket ?? {};
  const totalPages = meta.total ? Math.ceil(meta.total / PAGE_SIZE) : 1;

  useEffect(() => {
    mixpanel.track('Account Pending Bills Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useCsvButton({
    fileName: 'pending_bills.csv',
    fetchAll: async () =>
      (await getAccountPendingBillsReports({ ...filters, page: 1, limit: 1000 }))?.data ?? [],
    toLine: (r) => ({
      Against: r.against,
      Party: r.party ?? '',
      Project: r.project ?? '',
      Type: KIND_TEXT[r.kind] ?? r.kind,
      'Bill date': shortDate(r.bill_date),
      'Due date': shortDate(r.due_date),
      'Days overdue': Math.max(r.days_overdue, 0),
      Ageing: BUCKET_LABEL[r.bucket] ?? r.bucket,
      Billed: r.billed,
      Paid: r.paid,
      Pending: r.pending
    })
  });

  const closePay = () => {
    setPaying(null);
    setFormDirty(false);
  };

  return (
    <div className="flex w-full flex-col gap-4 bg-gray-100 px-3 py-4">
      <Loader isVisible={pending.isFetching && !pending.data} />

      <TileRow>
        <Tile
          variant="card"
          label="Total pending"
          hint={`${totals.count ?? 0} item${totals.count === 1 ? '' : 's'}${filters.bucket ? ', all ages' : ''}`}
          value={money(totals.amount ?? 0)}
          tone="warning"
          onClick={() => setFilters((f) => ({ ...f, bucket: '' }))}
        />
        {BUCKETS.map((b) => (
          <Tile
            key={b.value}
            variant="card"
            label={b.label}
            hint={`${byBucket[b.value]?.count ?? 0} item${byBucket[b.value]?.count === 1 ? '' : 's'}${
              filters.bucket === b.value ? ' · showing' : ''
            }`}
            value={money(byBucket[b.value]?.amount ?? 0)}
            tone={filters.bucket === b.value ? b.tone : 'neutral'}
            onClick={() =>
              setFilters((f) => ({ ...f, bucket: f.bucket === b.value ? '' : b.value }))
            }
          />
        ))}
      </TileRow>

      <FilterBar
        end={
          filtered && (
            <button
              type="button"
              onClick={clear}
              className="mb-2 text-sm font-semibold text-brand-700 hover:underline"
            >
              Clear
            </button>
          )
        }
      >
        <BillsFilterFields
          filters={filters}
          set={set}
          typed={typed}
          setTyped={setTyped}
          placeholder="Party, PI or bill"
        />
      </FilterBar>

      {pending.isError ? (
        <p className="text-sm text-danger-fg">
          Couldn&apos;t load pending bills:{' '}
          {pending.error?.response?.data?.message || pending.error?.message}
        </p>
      ) : (
        <TableCard>
          <Table>
            <THead>
              <tr>
                <Th>Against</Th>
                <Th>Party</Th>
                <Th>Project</Th>
                <Th>Type</Th>
                <Th>Due date</Th>
                <Th>Overdue</Th>
                <Th align="right">Billed</Th>
                <Th align="right">Paid</Th>
                <Th align="right">Pending</Th>
                <Th align="right">Action</Th>
              </tr>
            </THead>
            <TBody>
              {!rows.length && !pending.isFetching && (
                <EmptyRow colSpan={10}>
                  Nothing pending{filtered ? ' for these filters' : ''}.
                </EmptyRow>
              )}
              {rows.map((r) => (
                <tr key={r.key}>
                  <Td className="whitespace-nowrap font-medium text-gray-800">{r.against}</Td>
                  <Td>{r.party ?? '—'}</Td>
                  <Td className="text-gray-600">{r.project ?? '—'}</Td>
                  <Td>
                    <KindPill kind={r.kind} />
                  </Td>
                  <Td className="whitespace-nowrap text-gray-600">
                    {shortDate(r.due_date)}
                    {r.due_label && <div className="text-xs text-gray-500">{r.due_label}</div>}
                  </Td>
                  <Td className="whitespace-nowrap">
                    <Overdue days={r.days_overdue} />
                  </Td>
                  <Td align="right" numeric className="text-gray-600">
                    {money(r.billed)}
                  </Td>
                  <Td align="right" numeric className="text-gray-600">
                    {money(r.paid)}
                  </Td>
                  <Td align="right" numeric className="font-semibold">
                    {money(r.pending)}
                  </Td>
                  <Td align="right" className="whitespace-nowrap">
                    {r.pi_id ? (
                      <button
                        type="button"
                        onClick={() => setPaying(r)}
                        className="rounded-control border border-brand-700 px-3 py-1 text-sm font-semibold text-brand-700 hover:bg-brand-700 hover:text-white"
                      >
                        Record payment
                      </button>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </Td>
                </tr>
              ))}
            </TBody>
          </Table>
        </TableCard>
      )}

      {totalPages > 1 && (
        <PaginationComponent
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          isFetching={pending.isFetching}
        />
      )}

      {paying && (
        <Modal isDirty={formDirty} confirmOnClose={formDirty} width={50} onClose={closePay}>
          <div className="flex flex-col gap-4">
            <div className="pr-10">
              <h2 className="text-lg font-semibold text-gray-900">{paying.against}</h2>
              <p className="text-sm text-gray-600">
                {paying.party} · {paying.project} · {money(paying.pending)} due
              </p>
            </div>
            <RecordPaymentForm
              pi={{ id: paying.pi_id, po_id: paying.po_id, due_amount: paying.pending }}
              onDirty={setFormDirty}
              onBack={closePay}
              onDone={() => {
                closePay();
                pending.refetch();
                queryClient.invalidateQueries({ queryKey: ['accountPaidBills'] });
                queryClient.invalidateQueries({ queryKey: ['payables'] });
              }}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PendingBills;
